import React, { useEffect, useMemo, useRef, useState } from "react";

function uniqueOptions(options = []) {
  return Array.from(new Set((options || []).filter(Boolean).map((value) => String(value))));
}

export default function FreeTextCombobox({
  id,
  value,
  onChange,
  onBlur,
  options = [],
  placeholder,
  className = "",
  disabled = false,
}) {
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [showAllOptions, setShowAllOptions] = useState(true);

  const normalizedValue = String(value || "").trim().toLowerCase();
  const preparedOptions = useMemo(() => uniqueOptions(options), [options]);
  const filteredOptions = useMemo(() => {
    if (!normalizedValue || showAllOptions) {
      return preparedOptions;
    }
    return preparedOptions.filter((option) => option.toLowerCase().includes(normalizedValue));
  }, [normalizedValue, preparedOptions, showAllOptions]);

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setIsOpen(false);
        setShowAllOptions(true);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, []);

  const emitValue = (nextValue) => {
    if (typeof onChange === "function") {
      onChange({ target: { value: nextValue, type: "text" } });
    }
  };

  const handleInputChange = (event) => {
    setIsOpen(true);
    setShowAllOptions(false);
    if (typeof onChange === "function") {
      onChange(event);
    }
  };

  const handleFocus = () => {
    if (disabled) return;
    setIsOpen(true);
    setShowAllOptions(true);
  };

  const handleBlur = (event) => {
    if (typeof onBlur === "function") {
      onBlur(event);
    }
  };

  const handleToggle = () => {
    if (disabled) return;
    const nextOpen = !isOpen;
    setShowAllOptions(true);
    setIsOpen(nextOpen);
    if (nextOpen) {
      inputRef.current?.focus();
    } else {
      inputRef.current?.blur();
    }
  };

  const handleOptionMouseDown = (event) => {
    event.preventDefault();
  };

  const handleOptionSelect = (option) => {
    emitValue(option);
    setIsOpen(false);
    setShowAllOptions(true);
    inputRef.current?.focus();
  };

  return (
    <div
      ref={rootRef}
      className={`free-text-combobox${isOpen ? " free-text-combobox--open" : ""}${disabled ? " free-text-combobox--disabled" : ""}`}
    >
      <input
        ref={inputRef}
        id={id}
        type="text"
        value={value}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={`${className} free-text-combobox__input`.trim()}
        autoComplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        disabled={disabled}
      />
      <button
        type="button"
        className="free-text-combobox__toggle"
        onMouseDown={handleOptionMouseDown}
        onClick={handleToggle}
        tabIndex={-1}
        aria-hidden="true"
        disabled={disabled}
      >
        ▾
      </button>
      {isOpen && filteredOptions.length ? (
        <div className="free-text-combobox__listbox" role="listbox">
          {filteredOptions.map((option) => (
            <button
              key={option}
              type="button"
              className="free-text-combobox__option"
              onMouseDown={handleOptionMouseDown}
              onClick={() => handleOptionSelect(option)}
            >
              {option}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
